import { Box, Flex, FormControl, FormErrorMessage, FormLabel, Input, Select as CSelect, Switch, Text, Textarea } from '@chakra-ui/react'
import Select  from 'react-select';
import React, { useEffect, useState } from 'react'
import ProfilePictureCropper from '../input/ProfilePictureCropper'
import { DepartmentType, DesignationType, DoctorOverview, LanguageType } from '@/types';
import { userService } from '@/lib/services/userService';
import { Controller } from 'react-hook-form';

interface ContactInformationProps {
  register: any;
  errors: any;
  control: any;
  setValue: any;
  editingUser?: DoctorOverview | null;
}
const BLOOD_GROUP = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];


const ContactInformation: React.FC<ContactInformationProps> = ({ register, errors, control, setValue, editingUser = null }) => {
  const [languages, setLanguages] = useState<LanguageType[]>([]);
  const [department, setDepartment] = useState<DepartmentType[]>([]);
  const [designation, setDesignation] = useState<DesignationType[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setDepartment(await userService.getDepartment());
      setDesignation(await userService.getDesignation());
      setLanguages(await userService.getLanguages());
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (editingUser && department.length > 0 && designation.length > 0) {
      setValue('user_fullname', editingUser.user_fullname);
      setValue('user_login', editingUser.user_login);
      setValue('user_phone_number', editingUser.user_phone_number);
      setValue('user_email', editingUser.user_email);
      setValue('birthday', editingUser.birthday);
      setValue('years_of_experience', editingUser.years_of_experience);
      setValue('department_id', editingUser.department_id?.id?.toString());
      setValue('designation_id', editingUser.designation_id?.id?.toString());
      setValue('medical_license_number', editingUser.medical_license_number);
      setValue('blood_group', editingUser.blood_group);
      setValue('gender', editingUser.gender);
      setValue('bio', editingUser.bio);
      setValue('feature_on_website', editingUser.status === 1);
      
      // Set languages - convert to array of IDs
      if (editingUser.language && editingUser.language.length > 0) {
        const languageIds = editingUser.language.map(lang => lang.id.toString());
        setValue('language_id', languageIds);
      }
    }
  }, [editingUser, setValue, department, designation]);

  return (
    <><Box mb={4}>
              <Text fontWeight="medium">Profile Image</Text>
              <ProfilePictureCropper />
            </Box>
            <Flex content="space-between" align="start" gap={2} mt={2}>
              <FormControl isInvalid={!!errors.user_fullname}>
                <FormLabel fontWeight="medium">
                  Name{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input {...register("user_fullname")} placeholder="e.g. Dr. John Smith" />
                <FormErrorMessage>{errors.user_fullname?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.user_login}>
                <FormLabel fontWeight="medium">
                  Username{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input {...register("user_login")} placeholder="e.g. drjohnsmith" />
                <FormErrorMessage>{errors.user_login?.message}</FormErrorMessage>
              </FormControl>
            </Flex>

            <Flex content="space-between" align="start" gap={2} mt={2}>
              <FormControl isInvalid={!!errors.user_phone_number}>
                <FormLabel fontWeight="medium">
                  Phone Number{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input {...register("user_phone_number")} type="number" placeholder="e.g. 632345678900" />
                <FormErrorMessage>{errors.user_phone_number?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.user_email}>
                <FormLabel fontWeight="medium">
                  Email Address{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input {...register("user_email")} placeholder="e.g. doctor@example.com" />
                <FormErrorMessage>{errors.user_email?.message}</FormErrorMessage>
              </FormControl>
            </Flex>

            <Flex content="space-between" align="start" gap={2} mt={2}>
              <FormControl isInvalid={!!errors.birthday}>
                <FormLabel fontWeight="medium">
                  Date of Birth{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input {...register("birthday")} placeholder="YYYY-MM-DD" type="date" />
                <FormErrorMessage>{errors.birthday?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.years_of_experience}>
                <FormLabel fontWeight="medium">
                  Years of Experience{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  type="number"
                  {...register("years_of_experience", { valueAsNumber: true })}
                  placeholder="e.g. 5"
                />
                <FormErrorMessage>{errors.years_of_experience?.message}</FormErrorMessage>
              </FormControl>
            </Flex>
            <Flex content="space-between" align="start" gap={2} mt={2}>
              <FormControl isInvalid={!!errors.department_id}>
                <FormLabel fontWeight="medium">
                  Department{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <CSelect {...register("department_id")}>
                  <option value="">
                    Select
                  </option>
                  {department.map((dt: DepartmentType) => (
                    <option key={dt.id} value={dt.id}>
                      {dt.department}
                    </option>
                  ))}
                </CSelect>
                <FormErrorMessage>{errors.department_id?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.designation_id}>
                <FormLabel fontWeight="medium">
                  Designation{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <CSelect {...register("designation_id")}>
                  <option value="">
                    Select
                  </option>
                  {designation.map((dt: DesignationType) => (
                    <option key={dt.id} value={dt.id}>
                      {dt.designation}
                    </option>
                  ))}
                </CSelect>
                <FormErrorMessage>{errors.designation_id?.message}</FormErrorMessage>
              </FormControl>
            </Flex>
            <Flex content="space-between" align="start" gap={2} mt={2}>
              <FormControl isInvalid={!!errors.medical_license_number}>
                <FormLabel fontWeight="medium">
                  Medical License Number{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input {...register("medical_license_number")} placeholder="e.g. MD-123456" />
                <FormErrorMessage>{errors.medical_license_number?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.language_id} minW={0} >
              <FormLabel fontWeight="medium">Languages Spoken</FormLabel>

              <Controller
                control={control}
                name="language_id"
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    options={languages.map(lang => ({
                      value: lang.id,
                      label: lang.name,
                    }))}
                    value={languages
                      .filter(lang => field.value?.includes(lang.id.toString()))
                      .map(lang => ({ value: lang.id, label: lang.name }))}
                    onChange={(selected) =>
                      field.onChange(selected.map(opt => (opt.value).toString()))
                    }
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        width: '100%',     
                        maxWidth: '100%',  
                        minHeight: '40px',
                        height: '40px',
                        borderRadius: '6px',
                        borderColor: state.isFocused ? '#3182CE' : '#CBD5E0',
                        boxShadow: state.isFocused ? '0 0 0 1px #3182CE' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#3182CE' : '#A0AEC0',
                        },
                      }),

                      valueContainer: (base) => ({
                        ...base,
                        height: '38px',
                        padding: '0 8px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        flexWrap: 'nowrap', 
                      }),

                      multiValue: (base) => ({
                        ...base,
                        height: '24px',
                        alignItems: 'center',
                        flexShrink: 0, 
                      }),

                      multiValueLabel: (base) => ({
                        ...base,
                        whiteSpace: 'nowrap',
                        fontSize: '14px',
                        lineHeight: '24px',
                      }),

                      input: (base) => ({
                        ...base,
                        margin: 0,
                        padding: 0,
                      }),
                    }}
                  />
                )}
              />

              <FormErrorMessage>
                {errors.language_id?.message}
              </FormErrorMessage>
            </FormControl>
            </Flex>
            <Flex content="space-between" align="start" gap={2} mt={2}>
              <FormControl isInvalid={!!errors.blood_group}>
                <FormLabel fontWeight="medium">
                  Blood Group{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <CSelect placeholder="Select" {...register("blood_group")}>
                  {BLOOD_GROUP.map((bg) => (
                    <option key={bg} value={bg}>
                      {bg}
                    </option>
                  ))}
                </CSelect>
                <FormErrorMessage>{errors.blood_group?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.gender}>
                <FormLabel fontWeight="medium">
                  Gender{" "}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                  </FormLabel>
                <CSelect {...register("gender")} placeholder="Select">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </CSelect>
                <FormErrorMessage>{errors.gender?.message}</FormErrorMessage>
              </FormControl>
            </Flex>

            <FormLabel fontWeight="medium">Bio</FormLabel>
            <FormControl isInvalid={!!errors.bio}>
              <Textarea minH="100px" maxH="150px" {...register("bio")} placeholder="Short professional biography" />
              <FormErrorMessage>{errors.bio?.message}</FormErrorMessage>
            </FormControl>
            {/* <FormControl isInvalid={!!errors.feature_on_website} mt={2}>
              <FormLabel fontWeight="light" fontSize="xs">
                Feature on Website <Switch size="sm" {...register("feature_on_website")} />
              </FormLabel>
            </FormControl> */}
          </>
  )
}

export default ContactInformation